import { Box, Chip, IconButton, Link, Menu, MenuItem, TextField, Typography } from "@mui/material";
import AttachFile from "@mui/icons-material/AttachFile";
import Close from "@mui/icons-material/Close";
import DragIndicator from "@mui/icons-material/DragIndicator";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useRef, useState } from "react";
import { api } from "../../../api/API";
import { HtmlEditor } from "../../../misc/HtmlEditor";
import { useSnackAction } from "../../../misc/SnackAction";
import { isPersonalTeam, useWorkspace } from "../../../misc/Workspace";
import { AssigneePicker } from "../panel/AssigneePicker";
import { TaskPicker } from "../TaskPicker";
import { ChainFile, ChainLink, DraftRow, linkLabel, linkSeq } from "./chainRows";

// ChainRowEditor is one task of a chain: its title, date, assignees and what it waits
// on, and behind the expand toggle its description and attachments
export function ChainRowEditor(props: {
    rows: DraftRow[];
    index: number;
    team: number;
    autoFocus: boolean;
    onChange: (changes: Partial<DraftRow>) => void;
    onRemove: () => void;

    // onEnter adds the next row, the way enter does in a list
    onEnter: () => void;
}) {
    const row = props.rows[props.index];
    const seq = props.index + 1;

    const sortable = useSortable({ id: row.key });
    const ws = useWorkspace();
    const [linkMenu, setLinkMenu] = useState<HTMLElement | null>(null);
    const picker = useRef<HTMLInputElement>(null);

    // a file is uploaded as it's picked, and its id goes with the save
    const upload = useSnackAction(async (input: { files: File[] }) => {
        const added: ChainFile[] = [];
        for (const item of input.files) {
            const stored = await api.files.upload({ file: item });
            added.push({ id: stored.id, name: stored.name });
        }

        props.onChange({ files: row.files.concat(added) });
    }, { success: "Uploaded" });

    const taken = new Set(row.blockedBy.map(link => linkSeq(props.rows, props.index, link)));
    const otherRows = props.rows
        .map((other, index) => ({ other: other, seq: index + 1 }))
        .filter(item => item.seq !== seq && !taken.has(item.seq));

    const addLink = (link: ChainLink) => props.onChange({ blockedBy: row.blockedBy.concat(link) });

    return (
        <Box
            ref={sortable.setNodeRef}
            style={{
                transform: CSS.Transform.toString(sortable.transform),
                transition: sortable.transition,
            }}
            sx={{
                display: "flex", gap: 1, alignItems: "flex-start",
                py: 1, borderBottom: 1, borderColor: "divider",
                bgcolor: sortable.isDragging ? "grey.50" : "background.paper",
                position: "relative",
                zIndex: sortable.isDragging ? 1 : undefined,
            }}
        >
            <IconButton size="small" ref={sortable.setActivatorNodeRef} {...sortable.attributes}
                        {...sortable.listeners} aria-label="Drag to reorder" sx={{ cursor: "grab", mt: 0.5 }}>
                <DragIndicator fontSize="small" />
            </IconButton>

            <Typography variant="body2" color="text.secondary" sx={{ width: 24, pt: 1, flexShrink: 0 }}>
                #{seq}
            </Typography>

            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Task title"
                        fullWidth
                        autoFocus={props.autoFocus}
                        value={row.title}
                        onChange={e => props.onChange({ title: e.target.value })}
                        onKeyDown={e => {
                            if (e.key !== "Enter") return;

                            e.preventDefault();
                            props.onEnter();
                        }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        sx={{ width: 170, flexShrink: 0 }}
                        value={row.dueDate}
                        onChange={e => props.onChange({ dueDate: e.target.value })}
                    />
                </Box>

                {/* on a personal team the task can only be the user's */}
                {!isPersonalTeam(ws.teams, props.team) && (
                    <AssigneePicker team={props.team} value={row.assignedTo}
                                    onChange={value => props.onChange({ assignedTo: value })} />
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Blocked by</Typography>
                    {row.blockedBy.map((link, index) => {
                        const label = linkLabel(props.rows, props.index, link);
                        if (!label) return null;

                        return (
                            <Chip key={index} size="small" label={label}
                                  onDelete={() => props.onChange({
                                      blockedBy: row.blockedBy.filter((_, at) => at !== index),
                                  })} />
                        );
                    })}

                    {otherRows.length > 0 && (
                        <Link component="button" type="button" variant="caption" underline="hover"
                              onClick={e => setLinkMenu(e.currentTarget)}>
                            + Row
                        </Link>
                    )}

                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <TaskPicker
                            placeholder="Search tasks by # or title"
                            exclude={row.blockedBy.flatMap(link => link.kind === "task" ? [link.id] : [])}
                            onPick={task => addLink({ kind: "task", id: task.id, title: task.title })}
                        />
                    </Box>

                    <Menu anchorEl={linkMenu} open={!!linkMenu} onClose={() => setLinkMenu(null)}>
                        {otherRows.map(item => (
                            <MenuItem key={item.other.key} onClick={() => {
                                setLinkMenu(null);
                                addLink({ kind: "row", key: item.other.key });
                            }}>
                                {item.seq} {item.other.title || "Untitled"}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                {row.expanded && (
                    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                        <HtmlEditor minHeight={100} placeholder="Description"
                                    value={row.description}
                                    onChange={value => props.onChange({ description: value })} />

                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, p: 1 }}>
                            {row.files.map(file => (
                                <Chip key={file.id} size="small" icon={<AttachFile />} label={file.name}
                                      onDelete={() => props.onChange({
                                          files: row.files.filter(item => item.id !== file.id),
                                      })} />
                            ))}
                            <Link component="button" type="button" variant="caption" underline="hover"
                                  disabled={upload.loading} onClick={() => picker.current?.click()}>
                                Add a file
                            </Link>
                            <input ref={picker} type="file" multiple style={{ display: "none" }}
                                   onChange={e => {
                                       const files = Array.from(e.target.files || []);
                                       e.target.value = "";
                                       if (files.length > 0) upload.callback({ files: files });
                                   }} />
                        </Box>
                    </Box>
                )}
            </Box>

            <IconButton size="small" sx={{ mt: 0.5 }}
                        aria-label={row.expanded ? "Hide details" : "Show details"}
                        onClick={() => props.onChange({ expanded: !row.expanded })}>
                {row.expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <IconButton size="small" sx={{ mt: 0.5 }} aria-label="Remove row" onClick={props.onRemove}>
                <Close fontSize="small" />
            </IconButton>
        </Box>
    );
}
